import { createSignal, onCleanup, onMount } from "solid-js";

export interface ViewportBox {
  top: number;
  height: number;
  /** Soft keyboard (or other chrome) is eating the bottom of the screen. */
  keyboard: boolean;
}

/**
 * Tracks the visual viewport so a fixed full-screen layer ends exactly above the
 * soft keyboard (iOS keeps the layout viewport tall and only shrinks the visual one).
 * Falls back to window.innerHeight where visualViewport is missing.
 */
export function createViewportBox() {
  const read = (base: number): ViewportBox => {
    const vv = window.visualViewport;
    const height = Math.round(vv?.height ?? window.innerHeight);
    const top = Math.round(vv?.offsetTop ?? 0);
    return { top, height, keyboard: base - height > 120 };
  };
  let base = Math.round(window.visualViewport?.height ?? window.innerHeight);
  let baseWidth = window.innerWidth;
  const [box, setBox] = createSignal<ViewportBox>(read(base));

  onMount(() => {
    const vv = window.visualViewport;
    const update = () => {
      const h = Math.round(vv?.height ?? window.innerHeight);
      if (window.innerWidth !== baseWidth) {
        // rotation / window resize: forget the old "no keyboard" height
        baseWidth = window.innerWidth;
        base = h;
      } else {
        base = Math.max(base, h);
      }
      setBox(read(base));
    };
    update();
    vv?.addEventListener("resize", update);
    vv?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    onCleanup(() => {
      vv?.removeEventListener("resize", update);
      vv?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    });
  });

  return box;
}
